import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
// Components
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import {
  U21Button,
  U21Spacer,
  U21Subsection,
} from 'app/shared/u21-ui/components';
import { SidebarSummary } from 'app/modules/sidebar/components/utils/SidebarSummary';
import { DataLabelList } from 'app/modules/dataLabels/DataLabelList';
import { IconTag } from '@u21/tabler-icons';

// Models
import { ActionEventSidebarDefinition } from 'app/modules/sidebar/models';

// Selectors
import {
  selectEnrichmentConfig,
  selectRealTimeRulesEnabled,
} from 'app/modules/orgSettings/selectors';
import {
  selectDLEnabledForActionEventDetail,
  selectFraudTransactionRulesEnabled,
} from 'app/shared/featureFlags/selectors';
import { selectAgentOrgID } from 'app/modules/session/selectors';
import { selectEvaluationLog } from 'app/shared/eventsOld/selectors';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Constants
import { ROUTES_MAP } from 'app/shared/utils/routes';
import {
  clearEvaluationLog,
  retrieveEvaluationLogThunk,
} from 'app/shared/eventsOld/sliceEvents';
import { SidebarEvaluationLogInfo } from 'app/modules/sidebar/components/SidebarEvaluationLogInfo';
import { SummaryViewConfigTypeClassifier } from 'app/modules/summarySettings/types';
import { DeviceIntelligenceSidebarSection } from 'app/modules/sidebar/components/SidebarDeviceIntelligenceSection';
import { useGetActionEventDetail } from 'app/modules/actionEvents/queries/useGetActionEventDetail';
import { useGetShadowEvaluationLog } from 'app/modules/transactions/queries/useGetShadowEvaluationLog';
import { useExternalIdRedirect } from 'app/shared/hooks/useExternalIdRedirect';
import { ColdStorageCGDOMessage } from 'app/shared/components/DataLoader/ColdStorageCGDOMessage';

interface OwnProps {
  data: ActionEventSidebarDefinition['data'];
}

export const SidebarActionEvent = ({ data: { id, externalId } }: OwnProps) => {
  const [showEmptyFields, setShowEmptyFields] = useState(false);

  const evaluationLog = useSelector(selectEvaluationLog);
  const fraudTransactionRulesEnabled = useSelector(
    selectFraudTransactionRulesEnabled,
  );
  const dispatch = useDispatch();
  const { push } = useHistory();

  const isRTREnabled = useSelector(selectRealTimeRulesEnabled);

  const useCacheEndpoint = useSelector(selectDLEnabledForActionEventDetail);
  const orgId = useSelector(selectAgentOrgID);

  // In PG cache mode the detail endpoint is keyed on the hashed id derived from
  // external_id; otherwise fall back to the unit21 id.
  const detailId =
    externalId && useCacheEndpoint
      ? computeDlHashIdForCgdo(orgId, externalId)
      : `${id}`;
  const isValidId = detailId.length > 0 && detailId !== '0';
  const {
    data,
    isLoading,
    coldStorageLoading,
    query: { failureReason },
  } = useGetActionEventDetail(isValidId ? detailId : '');

  useExternalIdRedirect(failureReason);

  const actionEventTagIds = useMemo(
    () => data?.tags?.map((tag) => tag.id) ?? [],
    [data],
  );
  const isRtrOrFraudDetectionEnabled =
    fraudTransactionRulesEnabled || isRTREnabled;

  useEffect(() => {
    if (isRtrOrFraudDetectionEnabled && data?.external_id) {
      dispatch(clearEvaluationLog());
      dispatch(
        retrieveEvaluationLogThunk({
          id: data.external_id,
          eventType: 'action',
        }),
      );
    }
  }, [isRtrOrFraudDetectionEnabled, data?.external_id, dispatch]);

  const { data: shadowEvaluationLog } = useGetShadowEvaluationLog(
    isRtrOrFraudDetectionEnabled,
    data?.external_id,
    'action',
  );
  const isOnActionEventDetails = Boolean(
    useRouteMatch(ROUTES_MAP.dataExplorerActionEventsId.path),
  );
  const isDeviceIntelligenceEnabled = useSelector(selectEnrichmentConfig)
    .fingerprint?.enabled;

  return (
    <U21SideMenu
      onClose={() => {
        if (isOnActionEventDetails) {
          push(ROUTES_MAP.dataExplorerActionEvents.path);
        }
      }}
      footerLeftContent={
        <U21Button
          onClick={() => setShowEmptyFields((prev) => !prev)}
          variant="ghost"
        >
          {showEmptyFields ? 'Hide' : 'Show'} Empty Fields
        </U21Button>
      }
      loading={isLoading}
      noPadding
      title="Action Event Details"
    >
      {coldStorageLoading && (
        <ColdStorageMessageContainer>
          <ColdStorageCGDOMessage
            coldStorageStatus={coldStorageLoading}
            cgdoType="action_event"
          />
        </ColdStorageMessageContainer>
      )}
      <U21Spacer spacing={0.5}>
        <SidebarSummary
          classifier={SummaryViewConfigTypeClassifier.ACTION_EVENT}
          showEmptyFields={showEmptyFields}
          details={data}
        />
        {isDeviceIntelligenceEnabled && (
          <DeviceIntelligenceSidebarSection
            event="action"
            details={data}
            showEmptyFields={showEmptyFields}
          />
        )}
        <SidebarEvaluationLogInfo
          evaluationLog={evaluationLog}
          shadowEvaluationLog={shadowEvaluationLog}
        />
        <U21Subsection
          collapsed
          collapsible
          shadedHeader
          title="Tags"
          titleIcon={<IconTag />}
        >
          <U21Spacer>
            <DataLabelList tagIds={actionEventTagIds} />
          </U21Spacer>
        </U21Subsection>
      </U21Spacer>
    </U21SideMenu>
  );
};

const ColdStorageMessageContainer = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing(2)} 8px`};
`;
